import { useCallback, useState } from "react";
import { useEventListener } from "./useEventListener";

type SetValueAction<T> = T | ((prevState: T) => T);
type useLocalStorageResponse<T> = [T, (a: SetValueAction<T>) => void];
export function useLocalStorage<T>(
  key: string,
  initialValue: T = null,
  serialize: (obj: T) => string = JSON.stringify,
  deserialize: (str: string) => T = JSON.parse
): useLocalStorageResponse<T> {
  const readValue = useCallback(() => {
    let value = initialValue;
    try {
      const storageValue = localStorage.getItem(key);
      if (
        storageValue &&
        storageValue !== "null" &&
        storageValue !== "undefined"
      ) {
        value = deserialize(storageValue);
      }
    } catch (error) {
      console.log(error);
    }
    return value;
  }, [deserialize, initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = useCallback(
    (value: T) => {
      try {
        setStoredValue((currentValue) => {
          const valueToSet =
            typeof value === "function"
              ? (value as (current: T) => T)(currentValue)
              : value;

          // don't store null or undefined
          if (valueToSet == null) return valueToSet;

          if (serialize(valueToSet) === serialize(currentValue))
            return valueToSet;

          localStorage.setItem(key, serialize(valueToSet));

          window.dispatchEvent(new StorageEvent("local-storage", { key }));

          return valueToSet;
        });
      } catch (error) {
        console.log(error);
      }
    },
    [key, serialize]
  );

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // storage event only works for other documents, not the current one
  // so firing custom local-storage event for other hooks on this document
  useEventListener("storage", handleStorageChange);
  useEventListener("local-storage", handleStorageChange);

  return [storedValue, setValue];
}
